const ABSOLUTE_URL_RE = /^(https?:\/\/|data:|blob:)/i;

/**
 * True for a URL that must be used as-is: an absolute http(s) URL (e.g. a
 * presigned S3 link from the backend) or a local data:/blob: preview.
 */
export function isAbsoluteUrl(value: string): boolean {
  return ABSOLUTE_URL_RE.test(value);
}

/**
 * Resolves a backend-issued file reference into a usable URL.
 *
 * With STORAGE_DRIVER=s3 the backend returns absolute presigned URLs
 * (https://s3.../key?X-Amz-...). Those are signed as-is and must be used
 * exactly as received: no base prefix, no extra query params, no cache-buster.
 * With the local driver the value is still a relative "uploads/..." or
 * "/uploads/..." path, which gets the API origin prepended as before.
 * Mirrors epl_web's shared/lib/mediaUrl.ts.
 */
export function mediaUrl(path: string | null | undefined, base: string): string | null {
  if (!path) return null;
  if (isAbsoluteUrl(path)) return path;
  return `${base.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

/**
 * Same as mediaUrl() against the production API host, with a placeholder
 * image for an empty value.
 * @param imgUrl backend-issued path or absolute URL
 */
export function normalizeImageUrl(imgUrl: string | null | undefined): string {
  return mediaUrl(imgUrl, 'https://api.lk.e-pl.ru') ?? '/images/profile.png';
}

/**
 * Преобразует дату в формат для отправки на бэкенд (DD-MM-YYYY)
 * @param date Дата в любом формате
 * @returns Строка в формате DD-MM-YYYY
 */
export function toBackendDate(date: string | Date): string {
  if (date instanceof Date) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  // Если дата уже в формате DD-MM-YYYY, возвращаем как есть
  if (typeof date === 'string' && date.match(/^\d{2}-\d{2}-\d{4}$/)) {
    return date;
  }

  // Если дата в формате YYYY-MM-DD, преобразуем в DD-MM-YYYY
  if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = date.split('-');
    return `${day}-${month}-${year}`;
  }

  // Для других форматов создаем объект Date и преобразуем
  const d = new Date(date);
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}-${month}-${year}`;
}

interface IPersonName {
  lastName?: string | null;
  firstName?: string | null;
  middleName?: string | null;
}

// «Фамилия Имя Отчество»; пустые части (обычно отчество) пропускаются
export function fullName(person: IPersonName | null | undefined): string {
  if (!person) return '';
  return [person.lastName, person.firstName, person.middleName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
}
